import React, { useState, useEffect } from 'react';
import { listenToUnifiedCollection } from '../config/firebase';

export default function StudentProgressHistory({ currentUser, onNavigateToGrades }) {
  const [myActivities, setMyActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) return;

    const unsub = listenToUnifiedCollection("activities", (cloudActivities) => {
      const localActivities = JSON.parse(localStorage.getItem('motor_pedagogico_activities') || '[]');
      const allActivities = [...cloudActivities, ...localActivities];

      // Filtrar actividades asociadas al estudiante actual (por código o nombre)
      const filtered = allActivities.filter(act => {
        if (currentUser.code && act.id === currentUser.code) return true;
        if (currentUser.username && act.name && act.name.toLowerCase() === currentUser.username.toLowerCase()) return true;
        return false;
      });

      // Ordenar de más reciente a más antiguo
      filtered.sort((a, b) => {
        const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        return timeB - timeA;
      });

      // Eliminar duplicados exactos
      const uniqueMap = new Map();
      filtered.forEach(item => {
        const key = (item.id || '') + (item.topic || '') + (item.date || '') + (item.score || '');
        if (!uniqueMap.has(key)) {
          uniqueMap.set(key, item);
        }
      });

      setMyActivities(Array.from(uniqueMap.values()));
      setLoading(false);
    });

    return () => unsub();
  }, [currentUser]);

  if (!currentUser) {
    return (
      <div className="bg-slate-900/90 border border-slate-800 p-8 rounded-3xl text-center space-y-4 max-w-lg mx-auto shadow-2xl">
        <div className="text-4xl">🔒</div>
        <h3 className="text-xl font-bold text-white">Inicio de Sesión Requerido</h3>
        <p className="text-xs text-slate-300">
          Para ver tu historial de ejercicios, notas y puntos XP acumulados, debes iniciar sesión con tu Código Único EOH.
        </p>
      </div>
    );
  }

  // Cálculos de métricas del estudiante
  const totalCompleted = myActivities.length;
  const totalXP = myActivities.reduce((acc, act) => acc + (Number(act.xp) || 0), 0);
  const avgPct = totalCompleted > 0 
    ? Math.round(myActivities.reduce((acc, act) => acc + (Number(act.pct) || 0), 0) / totalCompleted) 
    : 0;

  // Calcular Nivel y Rango EOH
  let levelName = "Nivel 1: Principiante EOH";
  let levelBadge = "🌱";
  if (totalXP >= 1500) { levelName = "Nivel 5: Maestro Global EOH"; levelBadge = "👑"; }
  else if (totalXP >= 1000) { levelName = "Nivel 4: Experto Task-Based"; levelBadge = "⚡"; }
  else if (totalXP >= 600) { levelName = "Nivel 3: Explorador Avanzado"; levelBadge = "🚀"; }
  else if (totalXP >= 200) { levelName = "Nivel 2: Estudiante Activo"; levelBadge = "⭐"; }

  // Calcular progreso por Unidad (U1 a U5)
  const unitStats = [1, 2, 3, 4, 5].map(uNum => {
    const uActs = myActivities.filter(a => a.topic && a.topic.includes(`Unidad ${uNum}`));
    const count = uActs.length;
    const avg = count > 0 ? Math.round(uActs.reduce((acc, a) => acc + (Number(a.pct) || 0), 0) / count) : 0;
    return { unit: uNum, count, avg };
  });

  return (
    <div className="space-y-6 text-slate-100 animate-fadeIn">
      
      {/* ENCABEZADO DE MI HISTORIAL */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-700/80 pb-4">
        <div>
          <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-400 bg-emerald-950/80 px-2.5 py-0.5 rounded-full border border-emerald-500/30">
            Pasaporte Digital EOH
          </span>
          <h2 className="text-xl md:text-2xl font-black text-white mt-1">
            📊 Mi Historial de Actividades y Avance
          </h2>
          <p className="text-slate-400 text-xs mt-0.5">
            Estudiante: <strong className="text-white">{currentUser.username}</strong> | Código: <span className="text-emerald-300 font-mono">{currentUser.code}</span> | Grado: <span className="text-blue-300 font-bold">{currentUser.grade || '6°'}</span>
          </p>
        </div>

        {onNavigateToGrades && (
          <button
            onClick={onNavigateToGrades}
            className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-4 py-2 rounded-xl text-xs shadow-lg transition cursor-pointer self-start md:self-auto"
          >
            🚀 Ir a Resolver Misiones
          </button>
        )}
      </div>

      {/* TARJETAS RESUMEN DE DESEMPEÑO */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        
        <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-2xl text-center space-y-1 shadow-lg">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Misiones Completadas</div>
          <div className="text-2xl font-extrabold text-blue-400">{totalCompleted}</div>
          <span className="text-[10px] text-slate-500 block">Ejercicios evaluados</span>
        </div>

        <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-2xl text-center space-y-1 shadow-lg">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Puntos XP Totales</div>
          <div className="text-2xl font-extrabold text-amber-400">+{totalXP} XP</div>
          <span className="text-[10px] text-amber-500/80 font-bold block">Puntaje acumulado</span>
        </div>

        <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-2xl text-center space-y-1 shadow-lg">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Promedio de Aciertos</div>
          <div className="text-2xl font-extrabold text-emerald-400">{avgPct}%</div>
          <span className="text-[10px] text-slate-500 block">Desempeño promedio</span>
        </div>

        <div className="bg-slate-900/90 border border-slate-800 p-4 rounded-2xl text-center space-y-1 shadow-lg">
          <div className="text-xs font-bold text-slate-400 uppercase tracking-wider">Nivel EOH Actual</div>
          <div className="text-sm font-extrabold text-cyan-300 flex items-center justify-center gap-1 mt-1">
            <span>{levelBadge}</span> {levelName.split(':')[1] || levelName}
          </div>
          <span className="text-[10px] text-cyan-400/80 block font-semibold">{levelName.split(':')[0]}</span>
        </div>

      </div>

      {/* AVANCE POR UNIDADES ACADÉMICAS (U1 a U5) */}
      <div className="bg-slate-900/80 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-md">
        <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
          <span>📘</span> Avance Académico por Unidades (Grado {currentUser.grade || '6'}°)
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
          {unitStats.map((u, idx) => (
            <div key={idx} className="bg-slate-950/80 p-3 rounded-xl border border-slate-800 space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="font-extrabold text-blue-400">Unidad {u.unit}</span>
                <span className="text-[10px] font-bold text-slate-400">{u.count} resueltos</span>
              </div>

              <div className="w-full bg-slate-900 h-2 rounded-full overflow-hidden border border-slate-800">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-emerald-400 transition-all duration-300"
                  style={{ width: `${Math.min(u.avg, 100)}%` }}
                />
              </div>

              <div className="text-right text-[11px] font-bold text-emerald-400">
                {u.count > 0 ? `${u.avg}% aciertos` : 'Sin realizar'}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* TABLA DE HISTORIAL DETALLADO DE EJERCICIOS */}
      <div className="bg-slate-900/90 border border-slate-800 p-5 rounded-2xl space-y-4 shadow-xl">
        <div className="flex justify-between items-center">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-2">
            <span>📜</span> Registro de Misiones y Exámenes Realizados ({myActivities.length})
          </h3>
          <span className="text-[11px] text-emerald-400 font-medium">● Sincronizado en tiempo real</span>
        </div>

        {myActivities.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 uppercase text-[10px] bg-slate-950/60">
                  <th className="p-3">#</th>
                  <th className="p-3">Unidad / Tema</th>
                  <th className="p-3">Tipo de Actividad</th>
                  <th className="p-3">Puntaje</th>
                  <th className="p-3">Porcentaje</th>
                  <th className="p-3">Estado</th>
                  <th className="p-3">XP Ganados</th>
                  <th className="p-3">Fecha</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60 text-slate-200">
                {myActivities.map((act, index) => (
                  <tr key={index} className="hover:bg-slate-800/40 transition">
                    <td className="p-3 font-mono text-slate-400">{index + 1}</td>
                    <td className="p-3 font-bold text-white">{act.topic || 'Unidad General'}</td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        act.type && act.type.includes('ICFES') 
                          ? 'bg-purple-950/80 text-purple-300 border-purple-500/40' 
                          : 'bg-blue-950/80 text-blue-300 border-blue-500/40'
                      }`}>
                        {act.type || 'Taller 20 Ejercicios'}
                      </span>
                    </td>
                    <td className="p-3 font-mono font-bold text-slate-200">{act.score}</td>
                    <td className="p-3">
                      <span className={`font-bold ${
                        Number(act.pct) >= 80 ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {act.pct}%
                      </span>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                        Number(act.pct) >= 80 
                          ? 'bg-emerald-950 text-emerald-300 border-emerald-500/50' 
                          : 'bg-red-950 text-red-300 border-red-500/50'
                      }`}>
                        {act.status ? act.status : (Number(act.pct) >= 80 ? 'Aprobado' : 'No aprobado')}
                      </span>
                    </td>
                    <td className="p-3 font-bold text-amber-400">+{act.xp} XP</td>
                    <td className="p-3 text-slate-400">{act.date || 'Reciente'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="bg-slate-950/60 border border-slate-800/80 p-8 rounded-2xl text-center space-y-3">
            <div className="text-3xl">🚀</div>
            <h4 className="text-sm font-bold text-white">¡Aún no has completado ninguna misión!</h4>
            <p className="text-xs text-slate-400 max-w-md mx-auto">
              Selecciona tu grado desde el inicio y resuelve los talleres o exámenes ICFES para acumular tus primeros puntos XP y ver aquí tus calificaciones.
            </p>
            {onNavigateToGrades && (
              <button
                onClick={onNavigateToGrades}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-lg transition cursor-pointer mt-2"
              >
                Comenzar Misión Ahora 🎯
              </button>
            )}
          </div>
        )}
      </div>

    </div>
  );
}
